/**
 * Design B how-it-works route.
 * Desktop: pin the viewport, slide the track sideways, draw the line.
 * Phones: no pin, the vertical line draws as the steps scroll by.
 */
import { scene, revealAll, gsap, pinAnticipation } from '@theme/clinic-motion';

const headerOffset = () =>
  parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 64;

class ClinicRoute extends HTMLElement {
  connectedCallback() {
    this.dispose = scene(this, ({ scroller, desktop }) => {
      revealAll(this, { scroller });

      const viewport = this.querySelector('[data-route-viewport]');
      const track = this.querySelector('[data-route-track]');
      const path = this.querySelector(`[data-route-path="${desktop ? 'h' : 'v'}"]`);
      const steps = [...this.querySelectorAll('.clinic-route__step')];
      if (!viewport || !track) return;

      gsap.set(path, { strokeDashoffset: 1 });

      if (desktop) {
        const distance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: viewport,
            scroller,
            pin: true,
            pinType: 'fixed',
            anticipatePin: pinAnticipation(),
            start: () => `top ${headerOffset() + 40}`,
            end: () => `+=${distance() + window.innerHeight * 0.35}`,
            scrub: 0.6,
            invalidateOnRefresh: true,
          },
        });
        tl.to(track, { x: () => -distance(), ease: 'none' }, 0);
        tl.to(path, { strokeDashoffset: 0, ease: 'none' }, 0);
        steps.forEach((step, i) => {
          tl.fromTo(step, { autoAlpha: 0.25 }, { autoAlpha: 1, duration: 0.15, ease: 'none' }, (i / steps.length) * 0.85);
        });
        return () => tl.scrollTrigger?.kill();
      }

      const draw = gsap.to(path, {
        strokeDashoffset: 0,
        ease: 'none',
        scrollTrigger: { trigger: track, scroller, start: 'top 75%', end: 'bottom 55%', scrub: 0.5 },
      });
      steps.forEach((step) =>
        gsap.from(step, {
          autoAlpha: 0,
          x: 18,
          duration: 0.9,
          scrollTrigger: { trigger: step, scroller, start: 'top 85%', once: true },
        })
      );
      return () => draw.scrollTrigger?.kill();
    });
  }

  disconnectedCallback() {
    this.dispose?.();
  }
}

if (!customElements.get('clinic-route')) customElements.define('clinic-route', ClinicRoute);

